








































import { Component, mixins, Prop } from 'nuxt-property-decorator'
import { mdiMinus, mdiPlus } from '@mdi/js'
import MixinGeneral from '~/assets/mixins/general'
import ComponentIcon from '~/components/icon/icon.vue'

// Пауза после ввода, после которой число уходит, даже если человек не нажал
// Enter и не ушёл из поля. Короче нельзя: «123» набирают дольше, чем за 250 мс,
// и в корзину уходило «1».
const INPUT_DELAY = 800

/**
 * Счётчик количества: −, поле ввода, +. Общий для карточек товара и корзины.
 *
 * - Показывает то, что человек набрал или нажал, не дожидаясь сервера;
 *   пока поле в фокусе, ответ сервера его не перетирает.
 * - Набранное число уходит по Enter, при уходе из поля или через паузу.
 * - Запросы идут по одному; значения, набранные во время запроса, копятся,
 *   и следом уходит только последнее. Ввод никогда не блокируется.
 * - Если сервер ответил другим числом (урезал по остатку, отказал), после
 *   запроса поле показывает его.
 */
@Component({
  components: {
    ComponentIcon,
  },
})
export default class ComponentFormQuantityStepper extends mixins(MixinGeneral) {
  // Количество, подтверждённое сервером.
  @Prop({ type: Number, required: true }) readonly value!: number
  // Отправка количества; 0 — убрать товар. Счётчик ждёт её завершения.
  @Prop({ type: Function, required: true })
  readonly submit!: (quantity: number) => Promise<unknown>

  // Как у кнопки «В корзину»: '' — 40/36px, medium — 44px, large — 56/44px.
  @Prop({ type: String, default: '' }) readonly size!: string

  private mdiMinus = mdiMinus
  private mdiPlus = mdiPlus

  // Набранное в поле, пока его не зафиксировали.
  private draft: string | null = null
  // Количество, которое человек выбрал, но сервер ещё не подтвердил.
  private target: number | null = null
  private isSending = false
  private timer: ReturnType<typeof setTimeout> | null = null

  private get classSize() {
    return {
      [this.$style.large]: this.size === 'large',
      [this.$style.medium]: this.size === 'medium',
    }
  }

  private get currentValue() {
    return this.target ?? this.value
  }

  private get displayValue() {
    return this.draft ?? String(this.currentValue)
  }

  private onFocus(event: FocusEvent) {
    ;(event.target as HTMLInputElement).select()
  }

  private onInput(event: InputEvent) {
    const input = event.target as HTMLInputElement
    // Только цифры и один разделитель дроби (единицы вроде «рул» дробные).
    const value = input.value
      .replace(/[^\d.,]/g, '')
      .replace(',', '.')
      .replace(/(\..*)\./g, '$1')
    if (input.value !== value) {
      input.value = value
    }
    this.draft = value

    this.clearTimer()
    this.timer = setTimeout(() => this.commitDraft(), INPUT_DELAY)
  }

  private onBlur() {
    this.clearTimer()
    this.commitDraft()
    this.draft = null
  }

  // Пустое поле не отправляем — после ухода из поля вернётся прежнее число.
  private commitDraft() {
    if (this.draft === null || this.draft === '') {
      return
    }
    const quantity = Number(this.draft)
    if (!isNaN(quantity)) {
      this.setTarget(quantity)
    }
  }

  private step(delta: number) {
    // Нажали ±, не уходя из поля: сначала фиксируем набранное.
    this.clearTimer()
    this.commitDraft()
    this.draft = null
    this.setTarget(Math.max(this.currentValue + delta, 0))
  }

  private setTarget(quantity: number) {
    this.target = quantity
    this.flush()
  }

  private async flush() {
    if (this.isSending) {
      return
    }
    this.isSending = true
    try {
      while (this.target !== null && this.target !== this.value) {
        const quantity = this.target
        try {
          await this.submit(quantity)
        } catch (e) {
          // Не вышло — показываем то, что есть на сервере.
          this.target = null
          break
        }
        // Пока шёл запрос, ничего нового не выбрали — ждать нечего; поле
        // покажет ответ сервера.
        if (this.target === quantity) {
          this.target = null
        }
      }
      this.target = null
    } finally {
      this.isSending = false
    }
  }

  private clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  private beforeDestroy() {
    this.clearTimer()
  }
}
